import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import pluralize from 'pluralize';
import DatePicker from 'react-multi-date-picker';
import Textarea from 'react-textarea-autosize';
import dayjs from 'dayjs';

import CustomInput from '../CustomInput';
import TimelineCreateMemoryAssets from './MemoryAssets';
import { confirm } from '../Confirm';

import { formatDate } from '../../helpers';

import type { Asset } from '../../types/asset';
import type { TimelineMemoryForm } from '../../types/forms/timelinememoryform';

export interface ConfirmDialogProps {
  okLabel?: string;
  cancelLabel?: string;
  title?: string;
  confirmation?: string;
};

import '@awesome.me/webawesome/dist/components/accordion/accordion.js';

interface TimelineCreateMemoryProps {
  index: number;
  memory: TimelineMemoryForm;
  assets: Asset[];
  hovered: boolean;
  errors: any;
  setValue: Function;
  handleRemoveFile: Function;
  handleSplitFiles: Function;
};

const TimelineCreateMemory: React.FC<TimelineCreateMemoryProps> = ({
  index,
  memory,
  assets,
  hovered,
  errors,
  setValue,
  handleRemoveFile,
  handleSplitFiles,
}) => {
  const { control, register } = useForm({
    defaultValues: memory,
  });

  const handleChange = (e: any) => {
    setValue(`memories.${memory.id}.${e.target.name}`, e.target.value);
    setValue('lastUpdated', Date.now());
  };

  useEffect(() => {
    setValue(`memories.${memory.id}.id`, memory.id);
    setValue(`memories.${memory.id}.name`, memory.name);
    setValue(`memories.${memory.id}.title`, memory.title);
    setValue(`memories.${memory.id}.date`, memory.date);
    setValue(`memories.${memory.id}.content`, memory.content);
    setValue(`memories.${memory.id}.assets`, memory.assets);
    setValue(`memories.${memory.id}.removed`, false);
  }, []);

  const getErrorForField = (fieldName: string) => {
    const error = errors ? errors.find((err: any) => err.path.includes(fieldName)) : null;
    return error ? error.message || null : null;
  };

  const handleDeleteMemory = async () => {
    const confirmed = await confirm({
      confirmation: 'Are you sure you want to remove this memory from the timeline?',
      description: 'This memory will not be deleted, you will be able to access it from your Dashboard.',
      okLabel: 'Yes, remove it',
    } as any);

    if (confirmed) {
      setValue(`memories.${memory.id}.removed`, true);
      setValue('lastUpdated', Date.now());
    }
  };

  return (
    <div key={memory.id} className="timeline-memory">
      <wa-accordion>
        <wa-accordion-item className="swap-icon" expanded>
          <div slot="label" className="wa-split">
            <span className="timeline-memory-date wa-gap-xs wa-align-items-center">
              <wa-avatar initials={String(index + 1)} style={{ '--size': '2em' }}></wa-avatar>
              {memory.date ? formatDate(memory.date) : 'No date set'}
            </span>
            <span className="timeline-memory-files">
              {pluralize('file', memory.assets.length, true)}
            </span>
          </div>
          <i className="fa-regular fa-chevron-up" slot="icon" data-when="collapsed" />
          <i className="fa-regular fa-chevron-down" slot="icon" data-when="expanded" />
          <div className="timeline-memory-form wa-stack wa-gap-xs">
            <input
              type="hidden"
              {...register('id', { required: true, onChange: handleChange })}
              defaultValue={memory.id}
            />
            <TimelineCreateMemoryAssets
              id={memory.id}
              assets={assets}
              hovered={hovered}
              disabled={memory.editable === false}
              errors={errors}
              handleRemoveFile={handleRemoveFile}
              handleSplitFiles={handleSplitFiles}
            />
            <div className="wa-grid">
              <div>
                <label>Date of memory</label>
                <Controller
                  key={memory.id}
                  control={control}
                  {...register('date', { required: true, onChange: handleChange })}
                  defaultValue={memory.date}
                  disabled={memory.editable === false}
                  render={({ field }) => (
                    <>
                      <DatePicker
                        render={<CustomInput {...field} className={getErrorForField('date') ? 'invalid' : ''} />}
                        className="memory-about-date-picker"
                        value={dayjs(field.value).format('MM/DD/YYYY') || ''}
                        disabled={memory.editable === false}
                        onChange={(date) => {
                          field.onChange(date?.isValid ? date.format('YYYY-MM-DD') : '');
                        }}
                        format="MM/DD/YYYY"
                      />
                    </>
                  )}
                />
                {getErrorForField('date') && (
                  <span className="error-message">{getErrorForField('date')}</span>
                )}
              </div>
              <div>
                <label>Memory title</label>
                <wa-input
                  type="text"
                  {...register('title', { required: true, onChange: handleChange })}
                  disabled={memory.editable === false}
                  placeholder="Give this moment a name"
                  defaultValue={memory.title}
                  className={getErrorForField('title') ? 'invalid' : ''}
                  hint={getErrorForField('title') || ""}
                />
              </div>
              <div>
                <label>Your name</label>
                <wa-input
                  type="text"
                  {...register('name', { required: true, onChange: handleChange })}
                  disabled={memory.editable === false}
                  placeholder="Enter your name"
                  defaultValue={memory.name}
                  className={getErrorForField('name') ? 'invalid' : ''}
                  hint={getErrorForField('name') || ""}
                />
              </div>
            </div>
            <div className="wa-grid">
              <div className="timeline-memory-content">
                <label>Story (optional)</label>
                <Textarea
                  id="content"
                  minRows={3}
                  {...register('content', { onChange: handleChange })}
                  disabled={memory.editable === false}
                  placeholder="A sentence or two is enough..."
                  defaultValue={memory.content}
                />
              </div>
            </div>
            <div className="wa-split wa-justify-content-end">
              {memory.editable && (
                <wa-button
                  appearance="plain"
                  size="s"
                  onClick={handleDeleteMemory}
                >
                  <i className="fa-regular fa-trash-can" slot="start" />
                  Remove from timeline
                </wa-button>
              )}
            </div>
          </div>
        </wa-accordion-item>
      </wa-accordion>
    </div>
  );
}

export default TimelineCreateMemory;
